import { useCallback, useEffect, useState } from "react";
import { GridFilterModel } from "@mui/x-data-grid-pro";

const FILTER_STORAGE_KEY = (sku: string, gridName: string) => `grid-filters-${sku}-${gridName}`;

/**
 * Hook to manage and persist grid filter state
 * Saves filters to sessionStorage so they survive data refreshes
 * @param sku - Product SKU for unique filter storage per product
 * @param gridName - Unique name for the grid (e.g., "soh", "purchaseOrders")
 * @param defaultFilters - Default filters to use if none are saved
 */
export const useGridFilters = (
  sku: string,
  gridName: string,
  defaultFilters: GridFilterModel
) => {
  const [filters, setFilters] = useState<GridFilterModel>(defaultFilters);

  // Load filters from sessionStorage on mount
  useEffect(() => {
    try {
      const storageKey = FILTER_STORAGE_KEY(sku, gridName);
      const savedFilters = sessionStorage.getItem(storageKey);
      if (savedFilters) {
        setFilters(JSON.parse(savedFilters));
      }
    } catch (error) {
      console.error("Failed to load filters from sessionStorage:", error);
    }
  }, [sku, gridName]);

  // Save filters to sessionStorage whenever they change
  const updateFilters = useCallback((newFilters: GridFilterModel) => {
    try {
      setFilters(newFilters);
      const storageKey = FILTER_STORAGE_KEY(sku, gridName);
      sessionStorage.setItem(storageKey, JSON.stringify(newFilters));
    } catch (error) {
      console.error("Failed to save filters to sessionStorage:", error);
    }
  }, [sku, gridName]);

  // Clear filters from sessionStorage
  const clearFilters = useCallback(() => {
    try {
      setFilters(defaultFilters);
      const storageKey = FILTER_STORAGE_KEY(sku, gridName);
      sessionStorage.removeItem(storageKey);
    } catch (error) {
      console.error("Failed to clear filters from sessionStorage:", error);
    }
  }, [defaultFilters, sku, gridName]);

  return { filters, updateFilters, clearFilters };
};